import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import Icon from '../components/AppIcon';
import { logger } from '../utils/logger';

const EmailConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verifying your email...');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Extract token_hash and type from URL query parameters
        const tokenHash = searchParams?.get('token_hash');
        const type = searchParams?.get('type');
        const next = searchParams?.get('next'); // Get next redirect parameter

        if (!tokenHash || !type) {
          setStatus('error');
          setMessage('Invalid verification link. Missing required parameters.');
          logger?.error('Email verification failed: Missing token_hash or type parameter');
          return;
        }

        logger?.info('Verifying email with token', { type });

        // Call Supabase verifyOtp to verify the email token
        const { data, error } = await supabase?.auth?.verifyOtp({
          token_hash: tokenHash,
          type: type,
        });

        if (error) {
          setStatus('error');
          setMessage('Verification failed. The link may be expired or invalid. Please request a new verification email.');
          logger?.error('Email verification error', error);
          return;
        }

        if (data?.user) {
          // Check if this is a password recovery flow
          if (type === 'recovery' || type === 'magiclink') {
            setStatus('success');
            setMessage('Verification successful! Redirecting to password reset...');
            logger?.audit('PASSWORD_RECOVERY_VERIFIED', data?.user?.id, { email: data?.user?.email });
            
            // Redirect to reset password page or next parameter
            const redirectPath = next || '/reset-password';
            setTimeout(() => {
              navigate(redirectPath, { replace: true });
            }, 1500);
            return;
          }

          // User email was successfully verified (signup flow)
          setStatus('success');
          setMessage('Email verified successfully! Redirecting to sign in...');
          logger?.audit('EMAIL_VERIFIED', data?.user?.id, { email: data?.user?.email });
          
          // Start countdown
          const countdownInterval = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(countdownInterval);
                // Sign out the user so they can sign in properly
                supabase?.auth?.signOut()?.then(() => {
                  navigate('/sign-in', { 
                    replace: true,
                    state: { emailVerified: true, email: data?.user?.email }
                  });
                });
                return 0;
              }
              return prev - 1;
            });
          }, 1000);

          return () => clearInterval(countdownInterval);
        } else {
          // No user data returned - verification failed
          setStatus('error');
          setMessage('Verification link is invalid or expired. Please request a new verification email.');
          logger?.warn('Email verification failed: No user data returned');
        }
      } catch (error) {
        setStatus('error');
        setMessage('An unexpected error occurred. Please try again.');
        logger?.error('Email verification exception', error);
      }
    };

    verifyEmail();
  }, [navigate, searchParams]);

  const getIcon = () => {
    switch (status) {
      case 'success':
        return <Icon name="CheckCircle" size={64} className="text-green-600" />;
      case 'error':
        return <Icon name="XCircle" size={64} className="text-red-600" />;
      default:
        return <Icon name="Loader" size={64} className="text-primary animate-spin" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'success':
        return 'Email Verified!';
      case 'error':
        return 'Verification Failed';
      default:
        return 'Verifying Email...';
    }
  };

  return (
    <>
      <Helmet>
        <title>Email Confirmation - MyBodyToday</title>
        <meta name="description" content="Confirm your email address" />
      </Helmet>
      <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-lg shadow-lg border border-border p-8 text-center">
            <div className="flex justify-center mb-6">
              {getIcon()}
            </div>
            
            <h1 className="text-2xl font-bold text-foreground mb-4">
              {getTitle()}
            </h1>
            
            <p className="text-muted-foreground mb-6">
              {message}
            </p>

            {status === 'success' && countdown > 0 && (
              <p className="text-sm text-muted-foreground mb-4">
                Redirecting in {countdown} second{countdown !== 1 ? 's' : ''}...
              </p>
            )}
            
            {status === 'error' && (
              <div className="space-y-3">
                <Button
                  onClick={() => navigate('/sign-up', { replace: true })}
                  className="w-full"
                >
                  Back to Sign Up
                </Button>
                <Button
                  onClick={() => navigate('/sign-in', { replace: true })}
                  variant="outline"
                  className="w-full"
                >
                  Go to Sign In
                </Button>
              </div>
            )}
            
            {status === 'success' && (
              <Button
                onClick={() => {
                  supabase?.auth?.signOut()?.then(() => {
                    navigate('/sign-in', { replace: true });
                  });
                }}
                variant="outline"
                className="w-full"
              >
                Continue to Sign In
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailConfirmation;